import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styled from "@emotion/styled/macro";
import {
  Checkbox,
  LoadingIndicator,
  Modal,
  Radio,
  Select,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import { type SplatProspectUpdate } from "../../../../amplify/backend/function/OfferExpUsage/ts/public/types";
import {
  HeaderButton,
  HeaderContentLayout,
} from "components/templates/HeaderContentLayout";
import { Slideout } from "components/molecules/ConfirmInfo/Slideout";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { ErrorModal } from "components/molecules/ErrorModal";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useAuthToken } from "hooks/useAuthToken";
import { useProposals, useUsageData, useUtilityData } from "utils/swrHooks";
import { useOfferList } from "checkout/hooks/useCheckoutSwr";
import { patchUsage } from "services/prospect";
import { syncSalesforce } from "services/storeFront";
import { subscribeToAsyncRequest } from "utils/subscribeToAsyncRequest";
import { useNavigateToList } from "utils/useNavigateHelper";
import { SalesforceUpdatedContext } from "providers/NewOfferProvider";
import { logger } from "providers/logger";
import {
  HOME_TYPE_OPTIONS,
  PREFERRED_LANGUAGE_OPTIONS,
} from "constants/prospect";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import {
  ADDITIONAL_SYSTEM_OPTIONS,
  CUSTOMER_INFO_LABELS,
  EXISTING_SYSTEM_DETAILS_OPTIONS,
  fromExistingSystemDetailsOption,
  toExistingSystemDetailsOption,
} from "checkout/components/organisms/ConfirmHomeInfoTaskView/constants";

type CheckoutCustomerInfoSlideoutProps = {
  onClose: () => void;
  /** Required when used outside CheckoutContextProvider (e.g. Confirm Info). */
  prospectId?: string;
  /** When provided (e.g. Confirm Info), called after a successful save instead of navigating to the offer list. */
  onSuccess?: () => void | Promise<void>;
};

type CustomerInfoFormValues = {
  preferredLanguage: string;
  additionalSystem: boolean | undefined;
  existingSystemDetails: string;
  utilityCompany: string;
  homeType: string;
};

const createSlideoutButtons = ({
  onClose,
  onSave = () => undefined,
  isSaveDisabled,
  isSaveLoading = false,
  isCloseDisabled = false,
}: {
  onClose: () => void;
  onSave?: () => void;
  isSaveDisabled: boolean;
  isSaveLoading?: boolean;
  isCloseDisabled?: boolean;
}): HeaderButton[] => [
  {
    type: "secondary",
    text: "Cancel & Close",
    onClick: onClose,
    disabled: isCloseDisabled,
    dataTestId: "customer-info-cancel-and-close",
  },
  {
    type: "primary",
    text: "Save",
    onClick: onSave,
    disabled: isSaveDisabled,
    isLoading: isSaveLoading,
    dataTestId: "customer-info-save",
  },
];

const CustomerInfoErrorView = ({
  onClose,
  error,
  action,
}: {
  onClose: () => void;
  error: Error;
  action: string;
}) => (
  <Slideout onClose={onClose}>
    <div data-testid="checkout-customer-info-slideout">
      <HeaderContentLayout
        title="There was a Problem."
        description="Something went wrong. Please try refreshing the page."
        buttons={createSlideoutButtons({ onClose, isSaveDisabled: true })}
        content={
          <ErrorComponent
            context="CheckoutCustomerInfoSlideout"
            action={action}
            error={error}
          />
        }
      />
    </div>
  </Slideout>
);

const CheckoutCustomerInfoSlideout = ({
  onClose,
  prospectId: prospectIdProp,
  onSuccess,
}: CheckoutCustomerInfoSlideoutProps) => {
  const { prospectId: checkoutProspectId } = useCheckoutContext();
  const prospectId = prospectIdProp || checkoutProspectId;
  const authKey = useAuthToken("HYBRID")?.token || "";
  const cognitoToken = useAuthToken("IHD")?.token;
  const navigateToList = useNavigateToList();
  const salesforceUpdated = useContext(SalesforceUpdatedContext);

  const [initialValues, setInitialValues] =
    useState<CustomerInfoFormValues | null>(null);
  const [values, setValues] = useState<CustomerInfoFormValues | null>(null);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [hasAgreedToUpdate, setHasAgreedToUpdate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<{
    error: Error;
    action: string;
  } | null>(null);
  const [missingTokenError, setMissingTokenError] = useState<Error | null>(
    null
  );

  const {
    prospect,
    error: usageError,
    isLoading: isUsageLoading,
  } = useUsageData(prospectId);

  const {
    data: utilitiesOptions,
    error: utilityError,
    isLoading: isUtilitiesLoading,
  } = useUtilityData(prospect?.customerState, authKey);

  const { data: proposals, isLoading: isProposalsLoading } = useProposals(
    prospectId,
    authKey
  );
  const { offerList, isOfferListLoading } = useOfferList(
    prospectId,
    cognitoToken
  );
  const hasOffers = !!proposals?.length || !!offerList?.length;

  useEffect(() => {
    if (!prospect || initialValues) {
      return;
    }
    const loaded: CustomerInfoFormValues = {
      preferredLanguage: prospect.preferredLanguage ?? "",
      additionalSystem: prospect.additionalSystem,
      existingSystemDetails: toExistingSystemDetailsOption(
        prospect.existingSystemDetails,
        prospect.firstSystemOwnership
      ),
      utilityCompany: prospect.utilityCompany ?? "",
      homeType: prospect.homeType ?? "",
    };
    setInitialValues(loaded);
    setValues(loaded);
  }, [prospect, initialValues]);

  const utilityCompanyOptions = useMemo(
    () =>
      (Array.isArray(utilitiesOptions) ? utilitiesOptions : []).map(
        (utility) => ({
          label: utility.utilityName,
          value: utility.utilityName,
        })
      ),
    [utilitiesOptions]
  );

  const changedFields = useMemo<Partial<SplatProspectUpdate>>(() => {
    if (!values || !initialValues) {
      return {};
    }
    const changes: Partial<SplatProspectUpdate> = {};
    if (values.preferredLanguage !== initialValues.preferredLanguage) {
      changes.preferredLanguage =
        values.preferredLanguage as SplatProspectUpdate["preferredLanguage"];
    }
    if (values.additionalSystem !== initialValues.additionalSystem) {
      changes.additionalSystem = values.additionalSystem;
    }
    if (
      values.additionalSystem === true &&
      values.existingSystemDetails &&
      values.existingSystemDetails !== initialValues.existingSystemDetails
    ) {
      const mapped = fromExistingSystemDetailsOption(
        values.existingSystemDetails
      );
      changes.existingSystemDetails = mapped.existingSystemDetails;
      changes.firstSystemOwnership = mapped.firstSystemOwnership;
    }
    if (values.utilityCompany !== initialValues.utilityCompany) {
      changes.utilityCompany = values.utilityCompany;
    }
    if (values.homeType !== initialValues.homeType) {
      changes.homeType = values.homeType;
    }
    return changes;
  }, [values, initialValues]);

  const isDirty = Object.keys(changedFields).length > 0;
  const isExistingSystemDetailsMissing =
    values?.additionalSystem === true && !values.existingSystemDetails;

  const updateValue = useCallback(
    <TField extends keyof CustomerInfoFormValues>(
      field: TField,
      value: CustomerInfoFormValues[TField]
    ) => {
      setValues((prev) => (prev ? { ...prev, [field]: value } : prev));
    },
    []
  );

  const closeWarningModal = useCallback(() => {
    setIsWarningModalOpen(false);
    setHasAgreedToUpdate(false);
  }, []);

  const handleUpdate = useCallback(async () => {
    if (!cognitoToken) {
      const error = new Error(
        "Missing IHD session token, so Salesforce could not be synced."
      );
      logger.error("Cannot update customer info without an IHD token", {
        error,
        metadata: { prospectId },
      });
      closeWarningModal();
      setMissingTokenError(error);
      return;
    }

    setIsSaving(true);
    let action = "Updating Customer Info";
    try {
      const { asyncRequestId } = await patchUsage(authKey, prospectId, {
        ...changedFields,
        voidProposals: true,
      });
      await subscribeToAsyncRequest(authKey, asyncRequestId);
      if (hasOffers) {
        // pull the voided proposals before the rep lands on offer manager.
        action = "Syncing Salesforce";
        await syncSalesforce(cognitoToken, prospectId);
      }

      // createOrReuseOffer hands back the existing draft unless it is told which
      // opportunity fields changed, which would stamp a new offer with the
      // pre-edit values.
      action = "Refreshing New Offer";
      await salesforceUpdated(Object.keys(changedFields));

      // as long as there are no offers, call the onSuccess callback, else send to offer list
      if (onSuccess && !hasOffers) {
        onSuccess();
      } else {
        navigateToList();
      }
    } catch (err) {
      closeWarningModal();
      setSaveError({ error: err as Error, action });
    } finally {
      setIsSaving(false);
    }
  }, [
    authKey,
    cognitoToken,
    prospectId,
    changedFields,
    hasOffers,
    navigateToList,
    onSuccess,
    closeWarningModal,
    salesforceUpdated,
  ]);

  const handleSave = useCallback(() => {
    if (hasOffers) {
      setIsWarningModalOpen(true);
      return;
    }
    void handleUpdate();
  }, [hasOffers, handleUpdate]);

  const buttons = useMemo(
    () =>
      createSlideoutButtons({
        onClose,
        onSave: handleSave,
        isSaveDisabled: !isDirty || isSaving || isExistingSystemDetailsMissing,
        isSaveLoading: isSaving,
        isCloseDisabled: isSaving,
      }),
    [onClose, handleSave, isDirty, isSaving, isExistingSystemDetailsMissing]
  );

  if (usageError || utilityError) {
    return (
      <CustomerInfoErrorView
        onClose={onClose}
        error={(usageError || utilityError) as Error}
        action={usageError ? "Get Usage" : "Get Utility"}
      />
    );
  }

  if (missingTokenError) {
    return (
      <CustomerInfoErrorView
        onClose={onClose}
        error={missingTokenError}
        action="Sync Salesforce"
      />
    );
  }

  if (
    isUsageLoading ||
    isUtilitiesLoading ||
    isProposalsLoading ||
    isOfferListLoading ||
    !values
  ) {
    return (
      <Slideout>
        <LoadingIndicator fullScreen color="black" />
      </Slideout>
    );
  }

  return (
    <Slideout onClose={onClose}>
      <div data-testid="checkout-customer-info-slideout">
        {!!saveError &&
          createPortal(
            <PortalModalHost>
              <ErrorModal
                context="CheckoutCustomerInfoSlideout"
                error={saveError.error}
                action={saveError.action}
                onClose={() => setSaveError(null)}
              />
            </PortalModalHost>,
            document.body
          )}
        {isWarningModalOpen &&
          createPortal(
            <PortalModalHost>
              <Modal
                testId="customer-info-void-offers-modal"
                type="warning"
                title="Fixing now is better than re-signing"
                onClose={closeWarningModal}
                secondaryButton={{
                  text: "Cancel",
                  onClick: closeWarningModal,
                  disabled: isSaving,
                  testId: "customer-info-void-offers-cancel",
                }}
                primaryButton={{
                  text: "Update",
                  onClick: handleUpdate,
                  disabled: !hasAgreedToUpdate,
                  isLoading: isSaving,
                  testId: "customer-info-void-offers-update",
                }}
              >
                <WarningModalContent>
                  <Typography
                    variant="p"
                    color={tokens.TINTS_OFF_WHITE_20}
                    size={tokens.FONT_SIZE_3}
                  >
                    Updating this information will void your current offer and
                    you will need to save a new offer. This is better to do now
                    as it avoids the poor customer experience of a re-signing
                    later.
                  </Typography>
                  <Checkbox
                    checked={hasAgreedToUpdate}
                    onChange={(e) => setHasAgreedToUpdate(e.target.checked)}
                    disabled={isSaving}
                    label="I agree to update the information & make a new offer. I understand this update may take up to 60 seconds."
                    data-testid="customer-info-void-offers-acknowledge-checkbox"
                  />
                </WarningModalContent>
              </Modal>
            </PortalModalHost>,
            document.body
          )}
        <HeaderContentLayout
          title="Customer info"
          buttons={buttons}
          content={
            <InfoCard>
              <Fields>
                <Field>
                  <Select
                    label={CUSTOMER_INFO_LABELS.language}
                    testId="customer-info-preferred-language-select"
                    options={PREFERRED_LANGUAGE_OPTIONS}
                    placeholder="Select a language"
                    value={values.preferredLanguage}
                    disabled={isSaving}
                    onChange={(e) =>
                      updateValue("preferredLanguage", e.target.value)
                    }
                  />
                </Field>
                <Field>
                  <fieldset disabled={isSaving}>
                    <RadioGroupLabel>
                      <Typography
                        variant="span"
                        size="small"
                        color={tokens.TINTS_OFF_WHITE_20}
                      >
                        {CUSTOMER_INFO_LABELS.solarPanels}
                      </Typography>
                    </RadioGroupLabel>
                    <RadioGroup>
                      <Radio
                        name="additionalSystem"
                        label={ADDITIONAL_SYSTEM_OPTIONS.yes.label}
                        data-testid="customer-info-solar-panels-yes"
                        checked={values.additionalSystem === true}
                        disabled={isSaving}
                        onChange={() =>
                          updateValue(
                            "additionalSystem",
                            ADDITIONAL_SYSTEM_OPTIONS.yes.value
                          )
                        }
                      />
                      <Radio
                        name="additionalSystem"
                        label={ADDITIONAL_SYSTEM_OPTIONS.no.label}
                        data-testid="customer-info-solar-panels-no"
                        checked={values.additionalSystem === false}
                        disabled={isSaving}
                        onChange={() =>
                          updateValue(
                            "additionalSystem",
                            ADDITIONAL_SYSTEM_OPTIONS.no.value
                          )
                        }
                      />
                    </RadioGroup>
                  </fieldset>
                </Field>
                {values.additionalSystem === true && (
                  <Field>
                    <Select
                      label={CUSTOMER_INFO_LABELS.existingSystemDetails}
                      testId="customer-info-existing-system-details-select"
                      options={[...EXISTING_SYSTEM_DETAILS_OPTIONS]}
                      placeholder="Existing System Details"
                      value={values.existingSystemDetails}
                      disabled={isSaving}
                      onChange={(e) =>
                        updateValue("existingSystemDetails", e.target.value)
                      }
                    />
                  </Field>
                )}
                <Field>
                  <Select
                    label={CUSTOMER_INFO_LABELS.utilityCompany}
                    testId="customer-info-utility-company-select"
                    options={utilityCompanyOptions}
                    placeholder="Select a utility company"
                    value={values.utilityCompany}
                    disabled={isSaving}
                    onChange={(e) =>
                      updateValue("utilityCompany", e.target.value)
                    }
                  />
                </Field>
                <Field>
                  <Select
                    label={CUSTOMER_INFO_LABELS.homeType}
                    testId="customer-info-home-type-select"
                    options={HOME_TYPE_OPTIONS}
                    placeholder="Select a home type"
                    value={values.homeType}
                    disabled={isSaving}
                    onChange={(e) => updateValue("homeType", e.target.value)}
                  />
                </Field>
              </Fields>
            </InfoCard>
          }
        />
      </div>
    </Slideout>
  );
};

const InfoCard = styled.div`
  box-sizing: border-box;
  padding: 24px;
  background: ${tokens.TINTS_OFF_WHITE_80};
  border: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  border-radius: 8px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Field = styled.div`
  width: 356px;
  max-width: 100%;

  fieldset {
    margin: 0;
    padding: 0;
    border: 0;
  }

  @media (max-width: ${mobileMaxWidth}px) {
    width: 100%;
  }
`;

const RadioGroupLabel = styled.legend`
  display: block;
  padding: 0;
  margin-bottom: 6px;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  input[type="radio"]:disabled + label {
    color: ${tokens.TINTS_OFF_WHITE_40};
    cursor: not-allowed;
  }
`;

const WarningModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

/** Above Slideout overlay (z-index 1001) so portaled modals center on the viewport. */
const PortalModalHost = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
`;

export { CheckoutCustomerInfoSlideout };
export type { CheckoutCustomerInfoSlideoutProps };
